import type { Meta, StoryObj } from '@storybook/angular';
import { expect } from 'storybook/test';
import { StarEmptyIconComponent } from '../../../app/core/svg-icons/star-empty-icon.component';

const meta: Meta<StarEmptyIconComponent> = {
  title: 'Atoms/Icons/StarEmptyIcon',
  component: StarEmptyIconComponent,
  parameters: {
    docs: {
      description: {
        component: 'An empty star icon component with customizable size and color. Perfect for rating systems where stars can be filled or empty.',
      },
    },
  },
  argTypes: {
    width: {
      control: { type: 'text' },
      description: 'Width of the icon',
    },
    height: {
      control: { type: 'text' },
      description: 'Height of the icon',
    },
    color: {
      control: { type: 'color' },
      description: 'Stroke color of the star outline',
    },
  },
  args: {
    width: '24px',
    height: '24px',
    color: '#FDCA00',
  },
};

export default meta;
type Story = StoryObj<StarEmptyIconComponent>;

export const Default: Story = {
  args: {
    width: '24px',
    height: '24px',
    color: '#FDCA00',
  },
  render: (args) => ({
    props: args,
    template: `<app-star-empty-icon 
      [width]="width" 
      [height]="height" 
      [color]="color">
    </app-star-empty-icon>`,
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-star-empty-icon')).toBeTruthy();
  },
};

export const RatingSystem: Story = {
  render: () => ({
    template: `
      <div style="border: 1px solid #ddd; border-radius: 8px; padding: 2rem; max-width: 400px;">
        <h4>Barolo DOCG 2018</h4>
        <div style="display: flex; align-items: center; gap: 0.5rem; margin: 1rem 0;">
          <div style="display: flex; gap: 0.25rem;">
            <!-- 3 filled stars (using filled star icon concept) -->
            <div style="color: #FDCA00;">★★★</div>
            <!-- 2 empty stars -->
            <app-star-empty-icon width="20px" height="20px" color="#FDCA00"></app-star-empty-icon>
            <app-star-empty-icon width="20px" height="20px" color="#FDCA00"></app-star-empty-icon>
          </div>
          <span style="color: #666;">(3.0/5)</span>
        </div>
        <p style="color: #666; margin: 0;">En god vin med potentiale</p>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Empty star icons used in a product rating system alongside filled stars.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-star-empty-icon')).toBeTruthy();
  },
};

export const InteractiveRating: Story = {
  render: () => ({
    template: `
      <div style="text-align: center; padding: 2rem;">
        <h4>Bedøm denne vin</h4>
        <div style="display: flex; justify-content: center; gap: 0.25rem; margin: 1rem 0;">
          <app-star-empty-icon 
            width="32px" 
            height="32px" 
            color="#FDCA00" 
            style="cursor: pointer; transition: all 0.2s;"
            onmouseover="this.style.transform='scale(1.1)'"
            onmouseout="this.style.transform='scale(1)'">
          </app-star-empty-icon>
          <app-star-empty-icon 
            width="32px" 
            height="32px" 
            color="#FDCA00" 
            style="cursor: pointer; transition: all 0.2s;"
            onmouseover="this.style.transform='scale(1.1)'"
            onmouseout="this.style.transform='scale(1)'">
          </app-star-empty-icon>
          <app-star-empty-icon 
            width="32px" 
            height="32px" 
            color="#FDCA00" 
            style="cursor: pointer; transition: all 0.2s;"
            onmouseover="this.style.transform='scale(1.1)'"
            onmouseout="this.style.transform='scale(1)'">
          </app-star-empty-icon>
          <app-star-empty-icon 
            width="32px" 
            height="32px" 
            color="#FDCA00" 
            style="cursor: pointer; transition: all 0.2s;"
            onmouseover="this.style.transform='scale(1.1)'"
            onmouseout="this.style.transform='scale(1)'">
          </app-star-empty-icon>
          <app-star-empty-icon 
            width="32px" 
            height="32px" 
            color="#FDCA00" 
            style="cursor: pointer; transition: all 0.2s;"
            onmouseover="this.style.transform='scale(1.1)'"
            onmouseout="this.style.transform='scale(1)'">
          </app-star-empty-icon>
        </div>
        <p style="color: #666;">Klik på stjernerne for at bedømme</p>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Interactive empty star icons for user rating input.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-star-empty-icon')).toBeTruthy();
  },
};

export const ColorVariants: Story = {
  render: () => ({
    template: `
      <div style="display: flex; gap: 2rem; align-items: center; flex-wrap: wrap;">
        <div style="text-align: center;">
          <div>Gold (Default)</div>
          <app-star-empty-icon width="32px" height="32px" color="#FDCA00"></app-star-empty-icon>
        </div>
        <div style="text-align: center;">
          <div>Wine Red</div>
          <app-star-empty-icon width="32px" height="32px" color="#5C0A31"></app-star-empty-icon>
        </div>
        <div style="text-align: center;">
          <div>Gray</div>
          <app-star-empty-icon width="32px" height="32px" color="#6C757D"></app-star-empty-icon>
        </div>
        <div style="text-align: center;">
          <div>Orange</div>
          <app-star-empty-icon width="32px" height="32px" color="#F39C12"></app-star-empty-icon>
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Different color variants of the empty star icon.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-star-empty-icon')).toBeTruthy();
  },
};

export const SizeComparison: Story = {
  render: () => ({
    template: `
      <div style="display: flex; gap: 2rem; align-items: center;">
        <div style="text-align: center;">
          <div>Small (16px)</div>
          <app-star-empty-icon width="16px" height="16px" color="#FDCA00"></app-star-empty-icon>
        </div>
        <div style="text-align: center;">
          <div>Medium (24px)</div>
          <app-star-empty-icon width="24px" height="24px" color="#FDCA00"></app-star-empty-icon>
        </div>
        <div style="text-align: center;">
          <div>Large (32px)</div>
          <app-star-empty-icon width="32px" height="32px" color="#FDCA00"></app-star-empty-icon>
        </div>
        <div style="text-align: center;">
          <div>Extra Large (48px)</div>
          <app-star-empty-icon width="48px" height="48px" color="#FDCA00"></app-star-empty-icon>
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Different size variants of the empty star icon.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-star-empty-icon')).toBeTruthy();
  },
};
